import React, { memo, useCallback, useRef } from 'react';
import { getVerseText } from '../../utils/bibleData';
import { useTranslation } from '../../contexts/TranslationContext';

const BibleTextDisplay = memo(({
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  selectedReaders,
  matchingRecordings,
  currentlyPlayingVerse,
  verseNotes,
  onVerseTextClick,
  onAddNoteForVerse,
  onVerseLongPress,
  versesPerBlock = 3,
}) => {
  // Re-render against the active translation when it changes.
  const { version: translationVersion } = useTranslation();

  // Memoized verse data calculation
  const verses = useCallback(() => {
    if (!startVerse || !endVerse) return [];

    const verseData = [];
    for (let v = startVerse; v <= endVerse; v++) {
      verseData.push({
        number: v,
        text: getVerseText(selectedBook, selectedChapter, v),
        hasRecording: matchingRecordings.some(rec => rec.verse === v)
      });
    }

    return verseData;
  }, [selectedBook, selectedChapter, startVerse, endVerse, matchingRecordings, translationVersion]);

  // Filter verses by reader if needed
  const filteredVerses = useCallback(() => {
    const allVerses = verses();
    const shouldFilterByReader = selectedReaders && selectedReaders.length > 0;

    if (!shouldFilterByReader) return allVerses;
    
    const filtered = allVerses.filter(verse => verse.hasRecording);
    
    // If filtering by reader and no verses have recordings, show message
    if (filtered.length === 0) {
      return null; // Will be handled in render
    }
    
    return filtered;
  }, [verses, selectedReaders]);

  const versesToRender = filteredVerses();
  const touchStartPos = useRef(null);

  // Long-press (hold ~550ms without moving) opens "Add to Passages Plan" —
  // layered on top of the existing tap-to-play gesture below rather than
  // replacing it. longPressFiredRef suppresses the tap action that would
  // otherwise fire when the press is released.
  const LONG_PRESS_MS = 550;
  const MOVE_THRESHOLD = 10;
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const pressStartPos = useRef(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const startLongPress = useCallback((x, y, verseNumber) => {
    pressStartPos.current = { x, y };
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onVerseLongPress?.(verseNumber);
    }, LONG_PRESS_MS);
  }, [clearLongPressTimer, onVerseLongPress]);

  const cancelLongPress = useCallback(() => {
    clearLongPressTimer();
    pressStartPos.current = null;
  }, [clearLongPressTimer]);

  // Show message if no verses available for selected reader
  if (versesToRender === null) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-white/40">
        <p>No verses recorded by this reader in the selected passage.</p>
        <p className="text-sm mt-2">Try selecting a different reader or passage.</p>
      </div>
    );
  }

  return (
    <div className="text-gray-900 dark:text-white/90 text-lg leading-relaxed">
      {versesToRender.map((verse, index) => {
        const isHighlighted = currentlyPlayingVerse === verse.number;
        return (
          <span key={verse.number} data-verse={verse.number}>
            <span className="font-semibold text-gray-300 dark:text-white/25">
              {verse.number}
              {/* Note marker: coral when this verse has at least one family note */}
              {(() => {
                const hasNote = verseNotes[verse.number] > 0;
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddNoteForVerse(verse.number);
                    }}
                    className={`${hasNote ? 'text-coral hover:text-coral' : 'text-gray-300 dark:text-white/25 hover:text-cerulean dark:hover:text-cerulean-lighter'} transition-colors ml-1 mr-2`}
                    title={hasNote ? 'Note on this verse — tap to view or add' : 'Add note for this verse'}
                    aria-label={hasNote ? 'This verse has a note' : 'Add note for this verse'}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                );
              })()}
            </span>
             <span
               className={`${
                 isHighlighted
                   ? 'bg-blue-100 dark:bg-cerulean/25 text-space-cadet dark:text-white px-2 py-1 rounded-lg font-medium'
                   : 'text-gray-800 dark:text-white/85'
               } transition-all duration-200 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 hover:px-2 hover:py-1 hover:rounded-lg active:bg-gray-200 dark:active:bg-white/15 select-none`}
               onClick={(e) => {
                 e.preventDefault();
                 e.stopPropagation();
                 if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                 onVerseTextClick(verse.number);
               }}
               onMouseDown={(e) => startLongPress(e.clientX, e.clientY, verse.number)}
               onMouseUp={cancelLongPress}
               onMouseLeave={cancelLongPress}
               onTouchStart={(e) => {
                 touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                 startLongPress(e.touches[0].clientX, e.touches[0].clientY, verse.number);
               }}
               onTouchMove={(e) => {
                 const start = pressStartPos.current;
                 if (!start) return;
                 const dx = Math.abs(e.touches[0].clientX - start.x);
                 const dy = Math.abs(e.touches[0].clientY - start.y);
                 if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) clearLongPressTimer();
               }}
               onTouchEnd={(e) => {
                 clearLongPressTimer();
                 pressStartPos.current = null;
                 const start = touchStartPos.current;
                 touchStartPos.current = null;
                 if (longPressFiredRef.current) {
                   longPressFiredRef.current = false;
                   e.preventDefault();
                   e.stopPropagation();
                   return;
                 }
                 if (!start) return;
                 const dx = Math.abs(e.changedTouches[0].clientX - start.x);
                 const dy = Math.abs(e.changedTouches[0].clientY - start.y);
                 if (dx < MOVE_THRESHOLD && dy < MOVE_THRESHOLD) {
                   e.preventDefault();
                   e.stopPropagation();
                   onVerseTextClick(verse.number);
                 }
               }}
               title="Tap to play from this verse — hold to add to a Passage Plan"
             >
              {verse.text.replace(/^["']|["']$/g, '')}
            </span>
            {/* Space between verses within a block; break after every Nth verse (user preference) */}
            {(index + 1) % versesPerBlock !== 0 && index < versesToRender.length - 1 && ' '}
            {(index + 1) % versesPerBlock === 0 && index < versesToRender.length - 1 && (
              <div className="h-4"></div>
            )}
          </span>
        );
      })}
    </div>
  );
});

BibleTextDisplay.displayName = 'BibleTextDisplay';

export default BibleTextDisplay;
